import { useEffect, useState } from 'react';
import { api } from '../api/client';
import { confirmDialog } from '../utils/confirm';

const DIRECTIONS = ['Demande reçue', 'Demande envoyée'];
const STATUSES = ['En attente', 'Validée', 'Rejetée', 'Annulée'];
const STATUS_COLOR = { 'En attente': 'gold', 'Validée': 'green', 'Rejetée': 'red', 'Annulée': 'gray' };
const DATA_SCOPES = ['Dossier complet', 'Dossier administratif', 'Notes et historique scolaire', 'Situation financière', 'Discipline et assiduité', 'Dossier médical minimal'];
const REJECTION_REASONS = ['Identité élève non concordante', 'Autorisation parentale manquante', 'Dossier financier non soldé', 'Données insuffisantes', 'Établissement non souscripteur', 'Demande hors périmètre', 'Doublon de requête'];

const EMPTY = {
  requestDirection: 'Demande envoyée', requestingSchool: '', holdingSchool: '',
  studentId: '', studentFullName: '', studentMatricule: '', birthDate: '',
  sourceClass: '', targetClass: '', requestedDataScope: 'Dossier complet',
  requestDate: new Date().toISOString().slice(0, 10), status: 'En attente',
  decisionDate: '', rejectionReason: '', rejectionDetails: '',
  transferFee: 2500, saasBillable: 'Oui', auditNotes: '',
};

export default function Transferts() {
  const [rows, setRows] = useState([]);
  const [students, setStudents] = useState([]);
  const [error, setError] = useState('');
  const [modal, setModal] = useState(null);
  const [view, setView] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  function load() {
    api.get('/registry/student-transfers').then((d) => setRows((Array.isArray(d) ? d : (d?.records || [])).map((r) => ({ id: r.id, ...r.data })))).catch(() => {});
  }

  useEffect(() => {
    load();
    api.get('/students').then(setStudents).catch(() => {});
  }, []);

  const filtered = rows.filter((r) => {
    const q = search.toLowerCase();
    const matchQ = !q || (r.studentFullName || '').toLowerCase().includes(q) || (r.requestingSchool || '').toLowerCase().includes(q);
    const matchS = !statusFilter || r.status === statusFilter;
    return matchQ && matchS;
  });

  const stats = {
    recues: rows.filter((r) => r.requestDirection === 'Demande reçue').length,
    envoyees: rows.filter((r) => r.requestDirection === 'Demande envoyée').length,
    attente: rows.filter((r) => r.status === 'En attente').length,
    validees: rows.filter((r) => r.status === 'Validée').length,
  };

  async function updateStatus(id, status, extra = {}) {
    try {
      await api.patch(`/registry/student-transfers/${id}`, { status, decisionDate: new Date().toISOString().slice(0, 10), ...extra });
      load();
    } catch (e) { setError(e.message); }
  }

  async function approve(r) {
    if (!(await confirmDialog({ title: 'Valider la demande', message: `Valider le transfert de ${r.studentFullName} ?`, confirmLabel: 'Valider' }))) return;
    updateStatus(r.id, 'Validée');
  }

  async function reject(r) {
    const reason = prompt(`Motif de rejet :\n${REJECTION_REASONS.map((m, i) => `${i + 1}. ${m}`).join('\n')}`);
    if (!reason) return;
    const rejectionReason = REJECTION_REASONS.find((m) => m.toLowerCase().includes(reason.toLowerCase())) || reason;
    updateStatus(r.id, 'Rejetée', { rejectionReason });
  }

  return (
    <>
      <div className="kpi-grid">
        <div className="kpi"><span>Demandes reçues</span><strong>{stats.recues}</strong></div>
        <div className="kpi navy"><span>Demandes envoyées</span><strong>{stats.envoyees}</strong></div>
        <div className="kpi gold"><span>En attente</span><strong>{stats.attente}</strong></div>
        <div className="kpi green"><span>Validées</span><strong>{stats.validees}</strong></div>
      </div>

      <div className="panel">
        <div className="panel-head">
          <h2>Transferts inter-établissements</h2>
          <div style={{ display: 'flex', gap: 8 }}>
            <input className="search-input" placeholder="Élève, établissement…" value={search} onChange={(e) => setSearch(e.target.value)} style={{ width: 200 }} />
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={{ width: 140 }}>
              <option value="">Tous statuts</option>
              {STATUSES.map((s) => <option key={s}>{s}</option>)}
            </select>
            <button className="btn btn-sm" onClick={() => setModal({ ...EMPTY })}>+ Nouvelle demande</button>
          </div>
        </div>
        <div className="panel-body">
          {error && <div className="form-error">{error}</div>}
          {filtered.length === 0 ? <div className="empty">Aucune demande de transfert.</div> : (
            <div className="table-wrap"><table>
              <thead>
                <tr>
                  <th>Direction</th><th>Élève</th><th>Matricule</th>
                  <th>Étab. demandeur</th><th>Étab. détenteur</th>
                  <th>Données demandées</th><th>Date</th><th>Statut</th><th>Motif rejet</th><th></th>
                </tr>
              </thead>
              <tbody>{filtered.map((r) => {
                const locked = ['Validée', 'Rejetée', 'Annulée'].includes(r.status);
                const isReceived = r.requestDirection === 'Demande reçue';
                return (
                  <tr key={r.id}>
                    <td><span className={`badge ${isReceived ? 'navy' : 'gold'}`}>{r.requestDirection}</span></td>
                    <td><b>{r.studentFullName || '—'}</b></td>
                    <td className="mono">{r.studentMatricule || '—'}</td>
                    <td>{r.requestingSchool || '—'}</td>
                    <td>{r.holdingSchool || '—'}</td>
                    <td>{r.requestedDataScope || '—'}</td>
                    <td className="mono">{r.requestDate ? new Date(r.requestDate).toLocaleDateString('fr-FR') : '—'}</td>
                    <td><span className={`badge ${STATUS_COLOR[r.status] || 'gray'}`}>{r.status}</span></td>
                    <td style={{ maxWidth: 150, whiteSpace: 'normal', fontSize: '0.8rem' }}>{r.rejectionReason || '—'}</td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <button className="btn-ghost btn-sm" onClick={() => setModal(r)}>Détail</button>
                      {isReceived && !locked && (
                        <>
                          <button className="btn-ghost btn-sm" style={{ color: 'var(--success)' }} onClick={() => approve(r)}>✓</button>
                          <button className="link-danger" style={{ fontSize: '0.75rem' }} onClick={() => reject(r)}>✕</button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}</tbody>
            </table></div>
          )}
        </div>
      </div>

      {modal && (
        <TransfertModal
          initial={modal}
          students={students}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); load(); }}
        />
      )}
    </>
  );
}

function TransfertModal({ initial, students, onClose, onSaved }) {
  const isEdit = !!initial.id;
  const [f, setF] = useState({ ...EMPTY, ...initial });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  function onStudentChange(e) {
    const id = e.target.value;
    const s = students.find((s) => s.id === id);
    setF((p) => ({
      ...p, studentId: id,
      studentFullName: s ? `${s.lastName} ${s.firstName}` : p.studentFullName,
      studentMatricule: s?.matricule || p.studentMatricule,
      birthDate: s?.birthDate || p.birthDate,
    }));
  }

  async function save() {
    if (!f.requestingSchool || !f.holdingSchool) { setError('Les deux établissements sont obligatoires.'); return; }
    if (!f.studentFullName && !f.studentId) { setError('L\'élève est obligatoire.'); return; }
    setSaving(true);
    try {
      if (isEdit) await api.patch(`/registry/student-transfers/${initial.id}`, { data: f });
      else await api.post('/registry/student-transfers', { data: f, label: f.studentName || f.designation || f.reportedDisease || f.formType || f.eventType || '', searchText: JSON.stringify(f).slice(0, 200) });
      onSaved();
    } catch (e) { setError(e.message); } finally { setSaving(false); }
  }

  return (
    <div className="modal-overlay">
      <div className="modal modal-wide" onClick={(e) => e.stopPropagation()}>
        <h2>{isEdit ? 'Détail du transfert' : 'Nouvelle demande de transfert'}</h2>
        {error && <div className="form-error">{error}</div>}

        <div className="fieldset-title">Direction & établissements</div>
        <div className="grid-3">
          <div className="field"><label>Direction de la demande</label>
            <select value={f.requestDirection} onChange={set('requestDirection')}>
              {DIRECTIONS.map((d) => <option key={d}>{d}</option>)}
            </select>
          </div>
          <div className="field"><label>Établissement demandeur *</label>
            <input value={f.requestingSchool} onChange={set('requestingSchool')} />
          </div>
          <div className="field"><label>Établissement détenteur *</label>
            <input value={f.holdingSchool} onChange={set('holdingSchool')} />
          </div>
        </div>

        <div className="fieldset-title">Élève</div>
        <div className="grid-3">
          <div className="field"><label>Élève (depuis registre)</label>
            <select value={f.studentId} onChange={onStudentChange}>
              <option value="">Sélectionner…</option>
              {students.map((s) => <option key={s.id} value={s.id}>{s.lastName} {s.firstName}</option>)}
            </select>
          </div>
          <div className="field"><label>Nom complet saisi</label>
            <input value={f.studentFullName} onChange={set('studentFullName')} />
          </div>
          <div className="field"><label>Matricule</label>
            <input value={f.studentMatricule} onChange={set('studentMatricule')} className="mono" />
          </div>
          <div className="field"><label>Date de naissance</label>
            <input type="date" value={f.birthDate} onChange={set('birthDate')} />
          </div>
          <div className="field"><label>Classe origine</label>
            <input value={f.sourceClass} onChange={set('sourceClass')} />
          </div>
          <div className="field"><label>Classe destination</label>
            <input value={f.targetClass} onChange={set('targetClass')} />
          </div>
        </div>

        <div className="fieldset-title">Données & statut</div>
        <div className="grid-3">
          <div className="field"><label>Données demandées</label>
            <select value={f.requestedDataScope} onChange={set('requestedDataScope')}>
              {DATA_SCOPES.map((d) => <option key={d}>{d}</option>)}
            </select>
          </div>
          <div className="field"><label>Date de demande</label>
            <input type="date" value={f.requestDate} onChange={set('requestDate')} />
          </div>
          <div className="field"><label>Statut</label>
            <select value={f.status} onChange={set('status')}>
              {STATUSES.map((s) => <option key={s}>{s}</option>)}
            </select>
          </div>
          {['Rejetée'].includes(f.status) && (
            <div className="field"><label>Motif de rejet</label>
              <select value={f.rejectionReason} onChange={set('rejectionReason')}>
                <option value="">—</option>
                {REJECTION_REASONS.map((r) => <option key={r}>{r}</option>)}
              </select>
            </div>
          )}
          <div className="field"><label>Frais de transfert (XOF)</label>
            <input type="number" value={f.transferFee} onChange={set('transferFee')} />
          </div>
          <div className="field"><label>Module facturable</label>
            <select value={f.saasBillable} onChange={set('saasBillable')}>
              <option value="Oui">Oui</option>
              <option value="Non">Non</option>
            </select>
          </div>
        </div>
        <div className="field"><label>Notes / Journal</label>
          <textarea value={f.auditNotes} onChange={set('auditNotes')} rows={2} />
        </div>

        <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
          <button className="btn" onClick={save} disabled={saving} style={{ width: 'auto', padding: '0 22px' }}>{saving ? 'Enregistrement…' : 'Enregistrer'}</button>
          <button className="btn-ghost" onClick={onClose}>Annuler</button>
        </div>
      </div>
    </div>
  );
}

function ViewModal({ item, title, fields, onClose, onEdit }) {
  if (!item) return null;
  return (
    <div className="modal-overlay">
      <div className="modal modal-wide" onClick={(e) => e.stopPropagation()}>
        <h2>Fiche — {title}</h2>
        <div className="view-grid">
          {fields.map(([label, key]) => item[key] != null && item[key] !== '' ? (
            <div key={key} className="view-row">
              <span className="view-label">{label}</span>
              <span className="view-value">{String(item[key])}</span>
            </div>
          ) : null)}
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
          <button className="btn" onClick={onEdit} style={{ width: 'auto', padding: '0 22px' }}>Modifier</button>
          <button className="btn-ghost" onClick={onClose}>Fermer</button>
        </div>
      </div>
    </div>
  );
}
